import { Component, inject, ChangeDetectorRef, OnDestroy, PLATFORM_ID, PendingTasks } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IDocumentContentResponse, IDocumentType, ISection, ISeoDocument } from '../../core/models/documents.model';
import { StateService } from '../../core/services/state.service';
import { SeoService } from '../../core/services/seo.service';
import { isPlatformBrowser, DatePipe } from '@angular/common';
import { DayTime } from '../../shared/client-service/day-time/day-time';
import { Business } from "../../shared/components/pages/business/business";
import { MakeOrder } from "../../shared/components/pages/make-order/make-order";
import { toSignal } from '@angular/core/rxjs-interop';
import { Personal } from "../../shared/components/pages/personal/personal";
import { TenantConfigService } from '../../core/config/tenant-config';
import { UsStatesService } from '../../core/services/us-states.service';
import { Blogs } from "../../shared/components/pages/blogs/blogs";
import { MainPersonal } from "../../shared/components/pages/main-personal/main-personal";
import { GetCountryDocuments } from '../../core/services/get-country-documents';
import { IService } from '../../core/models/external-country.model';
import { US_STATE_CODES } from '../../core/constants/us-states';


export interface IFaqItem {
    question: string;
    answers?: string;
    answer?: string;
}

@Component({
    selector: 'app-daynamic-pages',
    imports: [Business, MakeOrder, Personal, Blogs, MainPersonal],
    templateUrl: './daynamic-pages.html',
    styleUrl: './daynamic-pages.scss',
})
export class DaynamicPages implements OnDestroy {
    private pendingTasks = inject(PendingTasks);
    private route = inject(ActivatedRoute);
    private stateService = inject(StateService);
    readonly sidebarSection = this.stateService.sidebarSection;
    seoContent!: IDocumentContentResponse;
    children!: IDocumentType[];
    sections!: ISection[];
    sectionsRows: ISection[][] = [];
    sectionsRow: ISection[] = []
    seoData: ISeoDocument | null = null
    private router = inject(Router);
    private tenantConfigService = inject(TenantConfigService);
    private usStatesService = inject(UsStatesService);
    private seoService = inject(SeoService);
    private getCountryDocsService = inject(GetCountryDocuments);
    private platformId = inject(PLATFORM_ID);
    private cdr = inject(ChangeDetectorRef);
    private dayTimeService = inject(DayTime);
    private datePipe = new DatePipe('en-US');

    varList = [
        "$businessPage",
        "$orderPAge",
        "$corporateTable",
        "$fdaTable",
        "$actionBtn",
        "$submitAQuestion",
        "$startOrderWthNoAction"
    ]

    get isBusinessDocsSlug(): boolean {
        // 1. Strip out query params or hash fragments (e.g., .../business-documents?page=2#top)
        const cleanUrl = this.router.url.split('?')[0].split('#')[0];

        // 2. Split into segments and remove any empty strings (handles trailing slashes safely)
        const segments = cleanUrl.split('/').filter(Boolean);

        // 3. Grab the very last slug
        const lastSlug = segments.pop();

        return lastSlug === 'business-documents';
    }



    async ngOnInit() {
        const taskCleanup = this.pendingTasks.add();
        try {
            let stateCode = this.route.snapshot.params['stateCode'] as string | undefined;
            
            if (!stateCode) {
                // During client-side navigation from the home page, StateService will be empty.
                // We must extract the state code directly from the URL.
                if (this.route.snapshot.url.length > 0) {
                    const firstSegment = this.route.snapshot.url[0].path.toLowerCase();
                    if (US_STATE_CODES.has(firstSegment)) {
                        stateCode = firstSegment;
                    }
                }
                
                // Fallback to stateService if still not found
                if (!stateCode) {
                    stateCode = this.stateService.stateCode();
                }
            }

            const isPersonalPage = this.sections?.some((s: ISection) => s.content?.includes('$singlePersonalOrder') || s.title?.includes('$singlePersonalOrder'));

            let fullStateName = stateCode || '';
            if (stateCode) {
                this.stateService.set(stateCode);
                if (this.usStatesService.usStates().length === 0) {
                    await this.usStatesService.loadUsStates();

                }
                // if (isPersonalPage) {
                //     await this.usStatesService.loadStateDetails(stateCode);
                // }
                const stateObj = this.usStatesService.usStates().find(s => s.code.toLowerCase() === stateCode?.toLowerCase() || s.slug.toLowerCase() === stateCode?.toLowerCase());
                if (stateObj) {
                    fullStateName = stateObj.name;
                }
            }

            const countryName = this.tenantConfigService.settings?.['CountryName'] || this.tenantConfigService.settings?.['Country Name'] || 'USA';

            const removeHash = (text: string) => {
                if (!text) return '';
                const state = this.usStatesService.currentStateDetails();
                return text
                    .replaceAll('#stateName', fullStateName)
                    .replaceAll('#countryName', countryName)
                    .replaceAll('#CountryName', countryName)
                    .replaceAll('stateApp', stateCode || '')
                    .replaceAll('#stateApostilleFee', state?.apostille_fee != null ? String(state.apostille_fee) : '')
                    .replaceAll('#stateApostilleDetails', state?.apostille_details || '')
                    .replaceAll('#stateProcessingTime', state?.processing_time || '')
                    .replaceAll('#stateExpeditedService', state?.expedited_service != null ? (state.expedited_service ? 'Yes' : 'No') : '')
                    .replaceAll('#stateExpeditedFee', state?.expedited_fee != null ? String(state.expedited_fee) : '')
                    .replaceAll('#stateAcceptedDocs', Array.isArray(state?.accepted_documents) ? state.accepted_documents.join(', ') : '')
                    .replaceAll('#stateOfficialSources', Array.isArray(state?.official_sources) ? state.official_sources.join(', ') : '')
                    .replaceAll('#statePaymentDetails', state?.payment_methods_details || '');
            };

            const data = this.route.snapshot.data['data'];
            this.seoContent = data?.layout;
            this.children = this.seoContent?.document_type.children ?? [];
            this.sections = this.seoContent?.document?.sections;
            
            let row = 0;
            if (this.sections) {
                const isSidebarSection = (element: ISection) => {
                    if (!element || !element.section_type) return false;
                    const st = element.section_type.toLowerCase();
                    return st === 'sidebar' || st === 'side_bar' || st === 'side-bar' || st.includes('sidebar');
                };

                const sidebar = this.sections.find(isSidebarSection);
                if (sidebar) {
                    sidebar.content = removeHash(sidebar.content || '');
                }
                this.stateService.sidebarSection.set(sidebar || null);

                const mainSections = this.sections.filter((element: ISection) => !isSidebarSection(element));

                mainSections.forEach((element: ISection) => {
                    element.content = removeHash(element.content || '');

                    if (element.row_sort_order == row) {
                        this.sectionsRow.push(element);
                    } else {
                        row += 1;
                        if (this.sectionsRow.length > 0) {
                            this.sectionsRows.push(this.sectionsRow);
                        }
                        this.sectionsRow = [];
                        this.sectionsRow.push(element);
                    }
                });
                if (this.sectionsRow.length > 0) {
                    this.sectionsRows.push(this.sectionsRow);
                }
                this.sectionsRow = [];
            }

            const isOrder = this.sectionsRows.length > 0 && this.sectionsRows[0].length > 0 && (this.sectionsRows[0][0].content || '').includes("$orderPAge");
            this.stateService.isOrderPage.set(isOrder);
            
            if (isPlatformBrowser(this.platformId)) {
                this.executeScriptsFromSections();
            }

            // ---- Apply SEO data from the API response ----
            const doc = this.seoContent?.document;
            if (doc) {
                // Apply the same variable replacements to SEO text fields
                const seoDoc: ISeoDocument = {
                    ...doc,
                    meta_title: removeHash(doc.meta_title || ''),
                    meta_description: removeHash(doc.meta_description || ''),
                    meta_keywords: removeHash(doc.meta_keywords || ''),
                };

                // If the API returns a redirect URL, navigate there instead of rendering
                if (seoDoc.redirect_url && isPlatformBrowser(this.platformId)) {
                    window.location.href = seoDoc.redirect_url;
                    return;
                }

                this.seoService.setPageSeo(seoDoc);
                this.seoData = seoDoc;
            }

            const priceData = await this.getCountryDocsService.fetchServicePricesIfNeeded(this.sectionsRows, this.seoData);
            
            if (priceData?.services && priceData.services.length > 0) {
                let totalTime = 0;
                let hasExp = false;
                let expDays = 0;

                priceData.services.forEach(service => {
                    if (service.ExpeditedOptions !== null) {
                        hasExp = true;
                        expDays += service.ExpeditedOptions.ExpeditedWorkingDays;
                    }
                    const time = service.ExpeditedOptions == null ? service.Time : service.ExpeditedOptions.ExpeditedWorkingDays;
                    totalTime += time;
                });

                const endDate = this.dayTimeService.calcEndDate(totalTime, hasExp, expDays);
                const endDay = this.datePipe.transform(endDate, 'EEE') || '';
                const endNum = this.datePipe.transform(endDate, 'd') || '';
                const endMonth = this.datePipe.transform(endDate, 'MMM') || '';

                const dateHtml = `
    <div class="text-center">
        <small>Expected to be shipped on</small>
        <div class="date dateCard mx-auto" style="width: 110px;">
            <p class="day mb-1">${endDay}</p>
            <p class="num mb-1" style="font-weight: 700;font-size: 3rem;line-height: 1;">${endNum}</p>
            <p class="month m-0">${endMonth}</p>
        </div>
    </div>`;

                this.sectionsRows.forEach((row: ISection[]) => {
                    row.forEach((section: ISection) => {
                        if (section.content) {
                            if (section.content.includes('#serviceIdFee') || section.content.includes('#serviceIdTime')) {
                                section.content = this.replaceServiceIdPlaceholders(section.content, priceData.services!);
                            }
                            if (section.content.includes('#addEndDate')) {
                                section.content = section.content.replaceAll('#addEndDate', dateHtml);
                            }
                        }
                    });
                });

                const sidebar = this.stateService.sidebarSection();
                if (sidebar?.content) {
                    let newContent = sidebar.content;
                    let updated = false;

                    if (newContent.includes('#serviceIdFee') || newContent.includes('#serviceIdTime')) {
                        newContent = this.replaceServiceIdPlaceholders(newContent, priceData.services!);
                        updated = true;
                    }
                    if (newContent.includes('#addEndDate')) {
                        newContent = newContent.replaceAll('#addEndDate', dateHtml);
                        updated = true;
                    }

                    if (updated) {
                        const updatedSidebar = {
                            ...sidebar,
                            content: newContent
                        };
                        this.stateService.sidebarSection.set(updatedSidebar);
                    }
                }
            }

            this.cdr.detectChanges();
        } finally {
            taskCleanup();
        }
    }

    

    private replaceServiceIdPlaceholders(content: string, services: IService[]): string {
        if (!content) return content;

        // Replace #serviceIdFee={{id}} or #serviceIdFee=id
        content = content.replace(/(\$)?#serviceIdFee=(?:\{\{)?(\d+)(?:\}\})?/g, (match, dollarPrefix, serviceIdStr) => {
            const targetId = Number(serviceIdStr);
            const found = services.find((s) => s.ServiceId === targetId || s.Id === targetId);
            if (found) {
                const fee = found.ExpeditedOptions == null
                    ? found.Fee
                    : (found.Fee + found.ExpeditedOptions.UsingExpeditedFees);
                return dollarPrefix ? `${fee}` : `$${fee}`;
            }
            return match;
        });

        // Replace #serviceIdTime={{id}} or #serviceIdTime=id
        content = content.replace(/#serviceIdTime=(?:\{\{)?(\d+)(?:\}\})?/g, (match, serviceIdStr) => {
            const targetId = Number(serviceIdStr);
            const found = services.find((s) => s.ServiceId === targetId || s.Id === targetId);
            if (found) {
                const time = found.ExpeditedOptions == null
                    ? found.Time
                    : found.ExpeditedOptions.ExpeditedWorkingDays;
                return `${time}`;
            }
            return match;
        });

        return content;
    }

    parseFaq(content: any): IFaqItem[] {
        if (!content) return [];
        if (Array.isArray(content)) return content;
        try {
            let parsed = typeof content === 'string' ? JSON.parse(content) : content;
            if (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error('Error parsing FAQ content:', e);
            return [];
        }
    }

    executeScriptsFromSections(): void {
        if (!isPlatformBrowser(this.platformId)) return;

        setTimeout(() => {
            if (!this.sections) return;

            this.sections.forEach((section) => {
                if (!section.content || !section.content.includes('<script')) return;

                const parser = new DOMParser();
                const doc = parser.parseFromString(section.content, 'text/html');
                const scripts = doc.querySelectorAll('script');

                scripts.forEach((script) => {
                    const newScript = document.createElement('script');
                    Array.from(script.attributes).forEach((attr) => {
                        newScript.setAttribute(attr.name, attr.value);
                    });
                    if (script.textContent) {
                        newScript.textContent = script.textContent;
                    }
                    document.body.appendChild(newScript);
                });
            });
        }, 100);
    }

    ngOnDestroy(): void {
        this.stateService.isOrderPage.set(false);
        this.stateService.sidebarSection.set(null);
        this.seoService.clearPageSeo();
    }
}
